import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MyDocumentsType } from "../../types/main";
import { FlexCol, FlexRow } from "../../utils/flexLayouts";
import { renderHighlightedText } from "../../utils/searchHighlight";
import { EmptyList } from "../EmptyList";
import Loading from "../Loading";
import DocumentSnippetIconTooltip from "../ui-icons/table/DocumentSnippetIconTooltip";
import SortIcon from "../ui-icons/table/SortIcon";
import { CriticallyListItem, TableHeaderColumnProps } from "../ui/table/Table";
import { UserAvatarItemStore } from "../ui/TGUserAvatarsList";
import { useAppSelector } from "src/hooks/redux";
import { getNameAndSurname } from "src/utils/users.utils";
import { User } from "src/modules/users/types";

const tableHeaderContent = {
  code: {
    title: "Код",
    sort: true,
    style: { width: "20%", position: "sticky" as const, left: 0, backgroundColor: "white", paddingLeft: "30px" },
  },
  aboutDocument: {
    title: "О документе",
    sort: false,
    style: { width: "40%" },
  },
  responsibleForRevision: {
    title: "Ответственный за пересмотр",
    sort: false,
    style: { width: "12%" },
  },
  publicationDate: {
    title: "Дата публикации",
    sort: true,
    style: { width: "12%" },
  },
  critical: {
    title: "Критичность",
    sort: false,
    style: { width: "10%" },
  },
};

const TableWithFilters = ({
  documents,
  cb,
  loading,
  hasNextPage,
}: {
  documents: MyDocumentsType[];
  cb: any;
  loading: boolean;
  hasNextPage?: boolean;
}) => {
  const limit = documents.length;

  return (
    <Table
      tableName="Table with filters"
      header={tableHeaderContent}
      body={documents}
      limit={limit}
      cb={cb}
      loading={loading}
      hasNextPage={hasNextPage}
    />
  );
};

export default TableWithFilters;

export type CodeType = { code: string; name: string };

export type TableBodyContentItem = {
  id: number;
  code: CodeType;
  aboutDocument: {
    collection: string;
    department: string;
    sector: string;
    process: string;
    type: string;
  };
  responsibleForRevision: {
    name: string;
    avatar: string | null;
    email: string;
    shortName: string;
  };
  publicationDate: string;
  critical: boolean;
};

function Table({
  header,
  body,
  tableName,
  limit,
  cb,
  loading,
  hasNextPage,
}: {
  header: typeof tableHeaderContent;
  body: TableBodyContentItem[];
  limit: number;
  tableName: string;
  cb: any;
  loading: boolean;
  hasNextPage?: boolean;
}) {
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc",
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (sortedField === "code") {
        return sortDirection === "asc"
          ? a[sortedField].code.localeCompare(b[sortedField].code)
          : b[sortedField].code.localeCompare(a[sortedField].code);
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (sortedField === "publicationDate") {
        return sortDirection === "asc"
          ? moment(a[sortedField], "DD.MM.YYYY").diff(moment(b[sortedField], "DD.MM.YYYY"))
          : moment(b[sortedField], "DD.MM.YYYY").diff(moment(a[sortedField], "DD.MM.YYYY"));
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body]) as TableBodyContentItem[];

  return (
    <TableList
      head={header}
      body={sortedTableBodyContent}
      sort={handleSort}
      sortDirection={sortDirection}
      sortedField={sortedField}
      cb={cb}
      loading={loading}
      hasNextPage={hasNextPage}
    />
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  cb,
  loading,
  hasNextPage,
}: {
  head: typeof tableHeaderContent;
  body: MyDocumentsType[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof typeof tableHeaderContent | null;
  sortDirection: "asc" | "desc" | "default";
  cb: any;
  loading: boolean;
  hasNextPage?: boolean;
}) {
  const tableFields = useMemo(() => {
    return Object.keys(head) as (keyof typeof tableHeaderContent)[];
  }, [head]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loading || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 500) {
      cb();
    }
  }, [cb, loading, hasNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div
      ref={scrollContainerRef}
      id="table-scroll"
      className="w-auto pb-5 pt-0 bg-white rounded-lg table-custom shadow-custom overflow-x-auto scrollbar scrollbar-h-2 scrollbar-thumb-[#D1D5DB] max-h-[calc(100vh-195px)]"
    >
      <FlexRow className="w-auto min-w-[930px]">
        <FlexCol className="relative grow pt-[20px] pl-[25px]">
          <FlexRow className="top-0 z-10 bg-white border-b sticky-custom pb-[20px] justify-between items-start">
            {tableFields.map((fieldKey) => {
              return (
                <TableHeaderColumn
                  key={fieldKey}
                  tableHeaderContent={head[fieldKey]}
                  sortDirection={sortedField === fieldKey ? sortDirection : "default"}
                  onClick={() => sort(fieldKey)}
                />
              );
            })}
          </FlexRow>
          <FlexRow className="">
            <FlexCol className="divide-y grow">
              {Array.isArray(body) && body.length > 0 ? (
                <div className="table-wrap">
                  {body.map((item) => (
                    <ListItem item={item} key={item.id} />
                  ))}
                </div>
              ) : loading ? (
                <div className="pt-4">
                  <Loading />
                </div>
              ) : (
                <EmptyList title="Документы не найдены" />
              )}
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </FlexRow>
    </div>
  );
}

function getUserNameWithPersonalNumber(user: User, defaultName: string) {
  return (
    getNameAndSurname(user.DisplayName, defaultName) +
    ` (${
      user.UserProfileProperties.find((u: any) => u.Key === "abiGlobalID").Value ||
      user.UserProfileProperties.find((u: any) => u.Key === "EmployeeNumber").Value ||
      "Нет номера"
    })`
  );
}

function ListItem({ item }: { item: MyDocumentsType }) {
  const [query, setQuery] = useSearchParams();
  const users = useAppSelector((state) => state.users.items);
  const titleHighlightedText = (item.searchMeta as any)?.titleHighlightedText;
  function openDocumentCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("documentCard", id.toString());

    setQuery(newSearchParams);
  }

  const user = users[item.responsibleForRevision.email];

  const userDisplayName =
    user && !!user.UserProfileProperties.length
      ? getUserNameWithPersonalNumber(user, item.responsibleForRevision.email)
      : user?.DisplayName || "";
  return (
    <FlexRow
      className="group relative justify-between hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg cursor-pointer [&>div]:py-2.5"
      key={item.id}
      onClick={() => openDocumentCard(item.id)}
    >
      <FlexCol
        className="break-words bg-white group-hover:bg-[#F9F9FA] pl-0 pr-0"
        style={{ width: "20%", position: "sticky" as const, left: 0 }}
      >
        <FlexRow className="items-start gap-x-[14px]">
          <FlexCol className="shrink-0 pt-0.5">
            <DocumentSnippetIconTooltip
              highlightedText={item.searchMeta?.highlightedText}
              foundBy={item.searchMeta?.foundBy}
            />
          </FlexCol>
          <FlexCol className="min-w-0 grow">
            <div>{renderHighlightedText(item.code.code)}</div>
            <div className="text-darkblue">
              {renderHighlightedText(
                (item.searchMeta?.foundBy === "title" || item.searchMeta?.foundBy === "both") &&
                  (titleHighlightedText || item.searchMeta?.highlightedText)
                  ? (titleHighlightedText || item.searchMeta?.highlightedText)
                      .replace(/<mark>/g, "[highlight]")
                      .replace(/<\/mark>/g, "[/highlight]")
                  : item.code.name,
              )}
            </div>
          </FlexCol>
        </FlexRow>
        {/* {hasContentSnippet && (
          <div className="mt-1 text-xs text-[#637381] line-clamp-2">
            {renderHighlightedText(item.searchMeta!.highlightedText)}
          </div>
        )} */}
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "40%" }}>
        <FlexRow className="gap-x-4">
          <FlexCol className="flex-[1.2_1]">Коллекция:</FlexCol>
          <FlexCol className="flex-1 text-black">{item.aboutDocument.collection}</FlexCol>
        </FlexRow>
        <FlexRow className="gap-x-4">
          <FlexCol className="flex-[1.2_1]">Подразделение/Функция:</FlexCol>
          <FlexCol className="flex-1 text-black">{item.aboutDocument.department}</FlexCol>
        </FlexRow>
        <FlexRow className="gap-x-4">
          <FlexCol className="flex-[1.2_1]">Колонна/Направление/Отдел:</FlexCol>
          <FlexCol className="flex-1 text-black">{item.aboutDocument.sector}</FlexCol>
        </FlexRow>
        <FlexRow className="gap-x-4">
          <FlexCol className="flex-[1.2_1]">Блок колонны/Участок:</FlexCol>
          <FlexCol className="flex-1 text-black">{item.aboutDocument.process}</FlexCol>
        </FlexRow>
        <FlexRow className="gap-x-4">
          <FlexCol className="flex-[1.2_1]">Тип:</FlexCol>
          <FlexCol className="flex-1 text-black">{item.aboutDocument.type}</FlexCol>
        </FlexRow>
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "12%" }}>
        <FlexRow className="gap-x-[5px] items-start">
          <FlexCol className="w-6">
            <UserAvatarItemStore item={item.responsibleForRevision} classNameText="hidden" single={true} />
          </FlexCol>
          <FlexCol className="shrink grow">{userDisplayName}</FlexCol>
        </FlexRow>
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "12%" }}>
        <span>{item.publicationDate}</span>
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "10%" }}>
        <CriticallyListItem item={item.critical} />
      </FlexCol>
    </FlexRow>
  );
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="justify-start select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium text-black whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}
